import {FC, useEffect, useMemo, MouseEvent as ReactMouseEvent, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Badge, Nav, Placeholder, NavDropdown} from 'react-bootstrap'
import {NavLink, useLocation, useNavigate} from 'react-router-dom'
import {
  Building,
  CardText,
  Gear,
  House,
  People,
  PersonCircle,
  Sliders,
  Trophy,
  BarChartLine,
  ListOl,
  CalendarEvent,
  FileText,
  Icon
} from 'react-bootstrap-icons'
import {getNavigationItems, filterNavByFeatureFlags, isNavItemActive, NavigationItem} from './utils/roleHierarchy'
import {announceToScreenReader, isPathActive} from './utils/navigationHelpers'
import {getTouchOptimizedStyles, triggerHapticFeedback} from './utils/touchOptimization'
import {useShouldAnimate} from './hooks/useReducedMotion'
import styles from './navigation.module.css'
import {useFriendRequestCount} from '../../contexts/FriendRequestCountContext'
import type {User, UserRole} from '../../types/auth'

const ICON_MAP: Record<string, Icon> = {
  dashboard: House,
  scorecards: CardText,
  events: CalendarEvent,
  competitions: Trophy,
  rankings: ListOl,
  friends: People,
  stats: BarChartLine,
  profile: PersonCircle,
  administration: Gear,
  settings: Sliders,
  users: People,
  tenants: Building,
  reports: FileText,
  analyticsAdmin: BarChartLine
}

interface NavItemSkeletonProps {
  vertical?: boolean;
}

const NavItemSkeleton: FC<NavItemSkeletonProps> = ({vertical = false}) => {
  const shouldAnimate = useShouldAnimate()

  return (
    <Nav.Item className={shouldAnimate ? styles.navItemSkeleton : ''}>
      <div className={`nav-link ${vertical ? 'py-3 px-4' : 'px-3 py-2'}`}>
        <Placeholder as="div" animation={shouldAnimate ? 'glow' : undefined} className="d-flex align-items-center">
          <Placeholder
            style={{
              width: vertical ? '24px' : '20px',
              height: vertical ? '24px' : '20px',
              borderRadius: '4px'
            }}
            className={vertical ? 'me-3' : 'me-2'}
          />
          <Placeholder xs={vertical ? 6 : 4}/>
        </Placeholder>
      </div>
    </Nav.Item>
  )
}

interface RoleBasedNavItemProps {
  item: NavigationItem & { label: string };
  vertical?: boolean;
  onClick?: (e: ReactMouseEvent<HTMLAnchorElement>) => void;
  isMobile?: boolean;
  showIcon?: boolean;
  index?: number;
  badgeCount?: number;
}

const RoleBasedNavItem: FC<RoleBasedNavItemProps> = ({
  item,
  vertical = false,
  onClick,
  isMobile = false,
  showIcon = true,
  index = 0,
  badgeCount = 0
}) => {
  const {t} = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [isNavigating, setIsNavigating] = useState(false)
  const shouldAnimate = useShouldAnimate()
  const isActive = item.path ? isPathActive(item.path, location.pathname) : false

  const IconComponent = showIcon ? ICON_MAP[item.key] : null
  const touchStyles = isMobile ? getTouchOptimizedStyles(true, 'menu') : {}

  const handleClick = (e: ReactMouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault()

    if (isMobile) {
      triggerHapticFeedback('light')
    }

    if (shouldAnimate) {
      setIsNavigating(true)
      announceToScreenReader(`Navigating to ${t(`navigation.roles.${item.role}.items.${item.key}`)}`)
      setTimeout(() => setIsNavigating(false), 500)
    }

    if (onClick) {
      onClick(e)
    }

    if (item.path) {
      navigate(item.path)
    }
  }

  // Check if item has children (dropdown menu)
  if (item.children && item.children.length > 0) {
    const isDesktop = !vertical && !isMobile
    const hasActiveChild = item.children.some(child => isNavItemActive(child, location.pathname))

    const renderDropdownRow = (navItem: NavigationItem, depth: number) => {
      const rowActive = navItem.path ? isPathActive(navItem.path, location.pathname) : false
      const RowIcon = showIcon ? ICON_MAP[navItem.key] : null

      const handleRowClick = (e: ReactMouseEvent<HTMLAnchorElement>) => {
        e.preventDefault()
        if (onClick) onClick(e)
        if (navItem.path) {
          navigate(navItem.path)
        }
      }

      return (
        <NavDropdown.Item
          key={navItem.key}
          as={NavLink}
          to={navItem.path || '#'}
          onClick={handleRowClick}
          className={`${rowActive ? 'active' : ''} ${vertical ? 'py-3 px-4 fs-5 text-dark border-bottom' : ''} ${depth > 0 ? styles.navSubItem : ''}`}
          data-testid={`nav-${navItem.key}`}
        >
          {RowIcon && <RowIcon size={vertical ? 24 : 16} className={vertical ? 'me-3' : 'me-2'} />}
          {t(`navigation.roles.${navItem.role}.items.${navItem.key}`)}
        </NavDropdown.Item>
      )
    }

    return (
      <NavDropdown
        title={
          <>
            {IconComponent && <IconComponent size={vertical ? 24 : 20} className={vertical ? 'me-3' : 'me-2'} />}
            <span>{t(`navigation.roles.${item.role}.items.${item.key}`)}</span>
          </>
        }
        id={`nav-dropdown-${item.key}`}
        className={`${vertical ? 'py-3 px-4 fs-5 text-dark border-bottom' : ''} ${styles.navItem} ${isDesktop ? styles.desktopNavItem : ''} ${hasActiveChild ? 'active' : ''}`}
        style={{
          animationDelay: shouldAnimate ? `${index * 50}ms` : '0ms',
          position: !vertical && !isMobile ? 'relative' : undefined
        }}
      >
        {item.children.flatMap((child) => [
          renderDropdownRow(child, 0),
          ...(child.children ?? []).map(grandchild => renderDropdownRow(grandchild, 1))
        ])}
      </NavDropdown>
    )
  }

  const isDesktop = !vertical && !isMobile
  const navLinkClasses = `nav-link ${vertical ? 'py-3 px-4 fs-5 text-dark border-bottom' : 'px-3 py-2'} d-flex align-items-center ${styles.navItem} ${isDesktop ? styles.desktopNavItem : ''} ${isActive ? 'active fw-semibold' : ''} ${isNavigating ? styles.loading : ''} ${vertical && isActive ? 'bg-light' : ''}`

  return (
    <Nav.Item
      className={shouldAnimate ? styles.navItem : ''}
      data-animate={shouldAnimate}
      style={{
        animationDelay: shouldAnimate ? `${index * 50}ms` : '0ms'
      }}
    >
      <Nav.Link
        as={NavLink}
        to={item.path || '#'}
        className={navLinkClasses}
        onClick={handleClick}
        style={touchStyles}
        aria-current={isActive ? 'page' : undefined}
        aria-busy={isNavigating}
        data-testid={`nav-${item.key}`}
      >
        {isActive && vertical && isMobile && (
          <span
            className="position-absolute start-0 top-0 bottom-0"
            style={{width: '4px', backgroundColor: 'var(--impact-text-accent)'}}
          />
        )}

        {IconComponent && (
          <IconComponent
            size={vertical ? 24 : 20}
            className={`${vertical ? 'me-3' : 'me-2'}`}
          />
        )}
        <span>
          {t(`navigation.roles.${item.role}.items.${item.key}`)}
        </span>

        {badgeCount > 0 && (
          <Badge
            bg="danger"
            pill
            className="ms-2"
            style={{fontSize: vertical ? '0.75rem' : '0.7rem'}}
          >
            {badgeCount}
          </Badge>
        )}

        {isNavigating && shouldAnimate && (
          <span
            className="ms-auto spinner-border spinner-border-sm text-primary"
            role="status"
            aria-hidden="true"
          />
        )}
      </Nav.Link>
    </Nav.Item>
  )
}

interface NavItemsProps {
  userRoles?: UserRole[];
  user?: User | null;
  vertical?: boolean;
  onItemClick?: (e: ReactMouseEvent<HTMLAnchorElement>) => void;
  isMobile?: boolean;
  showIcons?: boolean;
  isLoading?: boolean;
}

const NavItems: FC<NavItemsProps> = ({
  userRoles = [],
  user,
  vertical = false,
  onItemClick,
  isMobile = false,
  showIcons = true,
  isLoading = false
}) => {
  const {t, ready} = useTranslation()
  const [itemsLoaded, setItemsLoaded] = useState(false)
  const shouldAnimate = useShouldAnimate()
  const {count: friendRequestCount} = useFriendRequestCount()

  const navigationItems = useMemo(() => {
    const items = filterNavByFeatureFlags(getNavigationItems(userRoles), user)
    return items.map(item => ({
      ...item,
      label: t(`navigation.roles.${item.role}.items.${item.key}`),
      role: item.role
    }))
  }, [userRoles, user, t])

  if (ready && !itemsLoaded && !shouldAnimate) {
    setItemsLoaded(true)
  }

  useEffect(() => {
    if (ready && !itemsLoaded && shouldAnimate) {
      const timer = setTimeout(() => {
        setItemsLoaded(true)
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [ready, itemsLoaded, shouldAnimate])

  if (!ready || isLoading || !itemsLoaded) {
    if (!shouldAnimate && ready) {
      return (
        <Nav className={vertical ? 'flex-column w-100' : 'd-flex flex-row align-items-center'}>
          {navigationItems.map((item, index) => (
            <RoleBasedNavItem
              key={item.key}
              item={item}
              vertical={vertical}
              onClick={onItemClick}
              isMobile={isMobile}
              showIcon={showIcons}
              index={index}
            />
          ))}
        </Nav>
      )
    }

    return (
      <Nav className={vertical ? 'flex-column w-100' : 'd-flex flex-row align-items-center'}>
        {[1, 2, 3].map(index => (
          <NavItemSkeleton key={index} vertical={vertical}/>
        ))}
      </Nav>
    )
  }

  return (
    <Nav className={vertical ? 'flex-column w-100' : 'd-flex flex-row align-items-center'}>
      {navigationItems.map((item, index) => (
        <RoleBasedNavItem
          key={item.key}
          item={item}
          vertical={vertical}
          onClick={onItemClick}
          isMobile={isMobile}
          showIcon={showIcons}
          index={index}
          badgeCount={item.key === 'friends' ? friendRequestCount : 0}
        />
      ))}
    </Nav>
  )
}

export default NavItems
